import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
} from "@mui/material";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import LoginIcon from "@mui/icons-material/Login";
import LinkIcon from "@mui/icons-material/Link";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import GroupsIcon from "@mui/icons-material/Groups";
import DrawIcon from "@mui/icons-material/Draw";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, IconButton } from "@mui/material";
import { NeoButton } from "../../shared/components";
import ScheduleDelaySection from "./ScheduleDelaySection";
import CustomWorkflowApi from "../../apis/customWorkflowApi";

const customWorkflowApi = new CustomWorkflowApi();
const MAX_WELCOME_PDF_TEMPLATE_BYTES = 10 * 1024 * 1024;

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const commaIdx = result.indexOf(",");
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

type DefaultFieldSpec = { key: string; label: string; placeholder?: string; multiline?: boolean; minRows?: number };

const EMAIL_FORMAT_OPTIONS = [
  { value: "firstname.lastname", label: "firstname.lastname", exampleSuffix: "john.doe" },
  { value: "firstnamelastname", label: "firstnamelastname", exampleSuffix: "johndoe" },
  { value: "firstname_lastname", label: "firstname_lastname", exampleSuffix: "john_doe" },
  { value: "flastname", label: "f + lastname", exampleSuffix: "jdoe" },
  { value: "custom", label: "Custom", exampleSuffix: null },
];

interface StepConfigModalProps {
  open: boolean;
  onClose: () => void;
  stepLabel: string;
  stepId: string;
  options: Record<string, string>;
  defaultFields: DefaultFieldSpec[];
  onSave: (options: Record<string, string>) => void;
  isSetPassword?: boolean;
  isCreateUser?: boolean;
  isWelcomeEmail?: boolean;
  isItGluePassword?: boolean;
  isSetManager?: boolean;
  isAddToTeam?: boolean;
  isEmailSignature?: boolean;
  schedulerEnabled?: boolean;
  defaultDomainForCustomer?: string;
}

const checkboxSx = {
  "&.Mui-checked": { color: "#705D4A" },
  "& .MuiSvgIcon-root": { fontSize: 20 },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    fontFamily: "var(--theo-font-sans)",
    "&.Mui-focused fieldset": { borderColor: "#705D4A", borderWidth: 1.5 },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9a8a7a" },
    "& .MuiInputBase-input": { color: "#333", cursor: "text" },
  },
};

const StepConfigModal: React.FC<StepConfigModalProps> = ({
  open,
  onClose,
  stepLabel,
  stepId,
  options,
  defaultFields,
  onSave,
  isSetPassword,
  isCreateUser,
  isWelcomeEmail,
  isItGluePassword,
  isSetManager,
  isAddToTeam,
  isEmailSignature,
  schedulerEnabled = false,
  defaultDomainForCustomer,
}) => {
  const [localOptions, setLocalOptions] = React.useState<Record<string, string>>({});
  const [uploadingPdfTemplate, setUploadingPdfTemplate] = React.useState(false);
  const [pdfTemplateError, setPdfTemplateError] = React.useState("");

  const handleWelcomePdfTemplateFile = async (file: File | null) => {
    if (!file) return;
    setPdfTemplateError("");
    if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") {
      setPdfTemplateError("Please choose a PDF file.");
      return;
    }
    if (file.size > MAX_WELCOME_PDF_TEMPLATE_BYTES) {
      setPdfTemplateError("PDF template is too large (max 10MB).");
      return;
    }
    setUploadingPdfTemplate(true);
    try {
      const contentBase64 = await readFileAsBase64(file);
      const result = await customWorkflowApi.uploadPdfTemplate({
        filename: file.name,
        content_base64: contentBase64,
      });
      setLocalOptions((prev) => ({
        ...prev,
        welcome_pdf_template_object: result.object_name,
        welcome_pdf_template_filename: result.filename,
      }));
    } catch (error: any) {
      setPdfTemplateError(error?.response?.data?.error || error?.message || "Failed to upload PDF template.");
    } finally {
      setUploadingPdfTemplate(false);
    }
  };

  React.useEffect(() => {
    if (open) {
      const merged = { ...options };
      if (isCreateUser && !merged.email_domain?.trim() && defaultDomainForCustomer?.trim()) {
        merged.email_domain = defaultDomainForCustomer.trim();
      }
      setLocalOptions(merged);
    }
  }, [open, options, isCreateUser, defaultDomainForCustomer]);

  const handleChange = (key: string, value: string) => {
    setLocalOptions((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    const next = { ...localOptions };
    if (next.schedule_delay_enabled !== "true") {
      delete next.schedule_delay_amount;
      delete next.schedule_delay_unit;
      delete next.schedule_delay_reason;
    } else {
      if (!next.schedule_delay_unit) {
        next.schedule_delay_unit = "hours";
      }
      if (!String(next.schedule_delay_amount || "").trim()) {
        next.schedule_delay_amount = "1";
      }
    }
    onSave(next);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isWelcomeEmail || isItGluePassword || isEmailSignature ? "md" : "sm"}
      fullWidth
      disableEnforceFocus
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(112,93,74,0.12)",
          fontFamily: "var(--theo-font-sans)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 600,
          fontSize: "1.125rem",
          color: "#333",
          pb: 1.5,
          borderBottom: "1px solid #F0EDE9",
        }}
      >
        {isSetPassword && <VpnKeyIcon sx={{ color: "#705D4A", fontSize: 26 }} />}
        {isCreateUser && <PersonAddIcon sx={{ color: "#4CAF50", fontSize: 26 }} />}
        {isWelcomeEmail && <EmailIcon sx={{ color: "#0078D4", fontSize: 26 }} />}
        {isItGluePassword && <LockIcon sx={{ color: "#5D4037", fontSize: 26 }} />}
        {isSetManager && <SupervisorAccountIcon sx={{ color: "#5C6BC0", fontSize: 26 }} />}
        {isAddToTeam && <GroupsIcon sx={{ color: "#5558AF", fontSize: 26 }} />}
        {isEmailSignature && <DrawIcon sx={{ color: "#0078D4", fontSize: 26 }} />}
        Configure: {stepLabel}
      </DialogTitle>
      <DialogContent sx={{ fontFamily: "var(--theo-font-sans)", pt: 2.5, "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" } }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <ScheduleDelaySection
            enabled={schedulerEnabled}
            options={localOptions}
            onChange={(key, value) => handleChange(key, value)}
          />

          {isSetPassword && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#FAFAF9",
                border: "1px solid #F0EDE9",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1.5, color: "#555", fontSize: "0.8125rem", letterSpacing: "0.02em", textTransform: "uppercase" }}
              >
                Password options
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={localOptions.force_change_next_sign_in === "true"}
                      onChange={(e) => handleChange("force_change_next_sign_in", e.target.checked ? "true" : "false")}
                      sx={checkboxSx}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LoginIcon sx={{ fontSize: 18, color: "#888" }} />
                      <span>Force sign in on next login</span>
                    </Box>
                  }
                  sx={{ m: 0, "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.9375rem" } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={localOptions.generate_onetime_link === "true"}
                      onChange={(e) => handleChange("generate_onetime_link", e.target.checked ? "true" : "false")}
                      sx={checkboxSx}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LinkIcon sx={{ fontSize: 18, color: "#888" }} />
                      <span>Share one-time link</span>
                    </Box>
                  }
                  sx={{ m: 0, "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.9375rem" } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={localOptions.use_custom_password === "true"}
                      onChange={(e) => handleChange("use_custom_password", e.target.checked ? "true" : "false")}
                      sx={checkboxSx}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LockIcon sx={{ fontSize: 18, color: "#888" }} />
                      <span>Use custom password</span>
                    </Box>
                  }
                  sx={{ m: 0, "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.9375rem" } }}
                />
              </Box>
              {localOptions.use_custom_password === "true" && (
                <TextField
                  size="small"
                  type="password"
                  label="Custom password"
                  placeholder="Leave blank to use ticket value"
                  helperText="Optional. If empty, the ticket value will be used."
                  value={localOptions.custom_password || ""}
                  onChange={(e) => handleChange("custom_password", e.target.value)}
                  fullWidth
                  sx={{ ...textFieldSx, mt: 2 }}
                  inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                  InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                  FormHelperTextProps={{ style: { fontFamily: "var(--theo-font-sans)", fontSize: "0.75rem" } }}
                />
              )}
            </Box>
          )}

          {isCreateUser && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#FAFAF9",
                border: "1px solid #F0EDE9",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1.5, color: "#555", fontSize: "0.8125rem", letterSpacing: "0.02em", textTransform: "uppercase" }}
              >
                Email formulation rule
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 1.5, fontSize: "0.875rem" }}>
                When email is not present in the ticket, derive it from first name and last name.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localOptions.formulate_email_from_name === "true"}
                    onChange={(e) =>
                      handleChange("formulate_email_from_name", e.target.checked ? "true" : "false")
                    }
                    sx={checkboxSx}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EmailIcon sx={{ fontSize: 18, color: "#888" }} />
                    <span>Formulate email from first name and last name when not in ticket</span>
                  </Box>
                }
                sx={{ m: 0, mb: 1.5, "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.9375rem" } }}
              />
              {localOptions.formulate_email_from_name === "true" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5, pl: 4, borderLeft: "2px solid #E8E4E0" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontFamily: "var(--theo-font-sans)" }}>
                    Format
                  </Typography>
                  <RadioGroup
                    value={localOptions.email_format ?? "firstname.lastname"}
                    onChange={(e) => handleChange("email_format", e.target.value)}
                    sx={{ "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.875rem" } }}
                  >
                    {EMAIL_FORMAT_OPTIONS.map((opt) => {
                      const domain = localOptions.email_domain || defaultDomainForCustomer || "company.com";
                      const example = opt.exampleSuffix ? `e.g. ${opt.exampleSuffix}@${domain}` : null;
                      return (
                        <FormControlLabel
                          key={opt.value}
                          value={opt.value}
                          control={<Radio sx={{ "&.Mui-checked": { color: "#705D4A" } }} />}
                          label={opt.value === "custom" ? opt.label : `${opt.label} — ${example}`}
                        />
                      );
                    })}
                  </RadioGroup>
                  {localOptions.email_format === "custom" && (
                    <TextField
                      size="small"
                      fullWidth
                      label="Custom format"
                      placeholder="{{firstname}}.{{lastname}}"
                      helperText="Use {{firstname}} and {{lastname}} as placeholders"
                      value={localOptions.email_custom_format ?? ""}
                      onChange={(e) => handleChange("email_custom_format", e.target.value)}
                      sx={textFieldSx}
                      inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                      InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                      FormHelperTextProps={{ style: { fontFamily: "var(--theo-font-sans)", fontSize: "0.75rem" } }}
                    />
                  )}
                  <TextField
                    size="small"
                    fullWidth
                    label="Domain (optional)"
                    placeholder="e.g. company.com"
                    helperText="Leave blank to use tenant default domain"
                    value={localOptions.email_domain ?? ""}
                    onChange={(e) => handleChange("email_domain", e.target.value)}
                    sx={textFieldSx}
                    inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                    InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                    FormHelperTextProps={{ style: { fontFamily: "var(--theo-font-sans)", fontSize: "0.75rem" } }}
                  />
                </Box>
              )}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mt: 2.5,
                  mb: 1.5,
                  color: "#555",
                  fontSize: "0.8125rem",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                Initial password (create user)
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 1.5, fontSize: "0.875rem" }}>
                Passed to Microsoft Graph when creating the account. If off, Theo generates a strong password.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localOptions.use_custom_password === "true"}
                    onChange={(e) => handleChange("use_custom_password", e.target.checked ? "true" : "false")}
                    sx={checkboxSx}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LockIcon sx={{ fontSize: 18, color: "#888" }} />
                    <span>Use custom initial password</span>
                  </Box>
                }
                sx={{ m: 0, mb: 1, "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.9375rem" } }}
              />
              {localOptions.use_custom_password === "true" && (
                <TextField
                  size="small"
                  type="password"
                  label="Custom initial password"
                  placeholder="Leave blank to use ticket value"
                  helperText="Optional. If empty, use password from the ticket or ask the technician."
                  value={localOptions.custom_password || ""}
                  onChange={(e) => handleChange("custom_password", e.target.value)}
                  fullWidth
                  sx={{ ...textFieldSx, mb: 1.5 }}
                  inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                  InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                  FormHelperTextProps={{ style: { fontFamily: "var(--theo-font-sans)", fontSize: "0.75rem" } }}
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localOptions.generate_onetime_link === "true"}
                    onChange={(e) => handleChange("generate_onetime_link", e.target.checked ? "true" : "false")}
                    sx={checkboxSx}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LinkIcon sx={{ fontSize: 18, color: "#888" }} />
                    <span>Also generate one-time link for the initial password</span>
                  </Box>
                }
                sx={{ m: 0, mb: 1, "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.9375rem" } }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localOptions.force_change_next_sign_in !== "false"}
                    onChange={(e) =>
                      handleChange("force_change_next_sign_in", e.target.checked ? "true" : "false")
                    }
                    sx={checkboxSx}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LoginIcon sx={{ fontSize: 18, color: "#888" }} />
                    <span>Require password change on first sign-in</span>
                  </Box>
                }
                sx={{ m: 0, "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.9375rem" } }}
              />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mt: 2.5,
                  mb: 1.5,
                  color: "#555",
                  fontSize: "0.8125rem",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                Duplicate protection
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    // Missing option = on: the workflow default is to run the duplicate check.
                    checked={localOptions.check_recent_duplicates !== "false"}
                    onChange={(e) => handleChange("check_recent_duplicates", e.target.checked ? "true" : "false")}
                    sx={checkboxSx}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonSearchIcon sx={{ fontSize: 18, color: "#888" }} />
                    <span>Check for recently created duplicate users (past 7 days)</span>
                  </Box>
                }
                sx={{ m: 0, "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.9375rem" } }}
              />
              <Typography variant="body2" sx={{ color: "#666", mt: 0.75, ml: 4, fontSize: "0.8125rem" }}>
                Before creating the account, Theo checks whether a user with the same name, email alias, or UPN was
                created in this tenant in the past 7 days (for example from a duplicate ticket). If a match is found,
                the workflow pauses and shows the matching users for review instead of creating a second account.
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mt: 2.5,
                  mb: 1.5,
                  color: "#555",
                  fontSize: "0.8125rem",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                Requester verification
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    // Opt-in: requester verification needs a configured authorized list, so it is
                    // off unless explicitly enabled ("true").
                    checked={localOptions.verify_authorized_requester === "true"}
                    onChange={(e) => handleChange("verify_authorized_requester", e.target.checked ? "true" : "false")}
                    sx={checkboxSx}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <VerifiedUserIcon sx={{ fontSize: 18, color: "#888" }} />
                    <span>Verify the ticket requester is an authorized contact</span>
                  </Box>
                }
                sx={{ m: 0, "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.9375rem" } }}
              />
              <Typography variant="body2" sx={{ color: "#666", mt: 0.75, ml: 4, fontSize: "0.8125rem" }}>
                Before creating the account, Theo checks that the ticket requester is on this client&apos;s authorized
                requester list below. If the requester is not on the list (or the list is empty), the workflow pauses
                for review instead of creating the account — a forged onboarding request is an account-takeover vector.
              </Typography>
              {localOptions.verify_authorized_requester === "true" && (
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={4}
                  maxRows={16}
                  label="Authorized requester emails"
                  placeholder={"One per line (or comma separated); emails or @domains, e.g.\nhr@client.com\n@client.com"}
                  value={localOptions.authorized_requesters ?? ""}
                  onChange={(e) => handleChange("authorized_requesters", e.target.value)}
                  sx={{ ...textFieldSx, mt: 1.5 }}
                  inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                  InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                />
              )}
            </Box>
          )}

          {isWelcomeEmail && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#FAFAF9",
                border: "1px solid #F0EDE9",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: "#555",
                  fontSize: "0.8125rem",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                Welcome email template
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 2, fontSize: "0.8125rem" }}>
                Stored in the workflow. At send time, replace placeholders with values from the ticket and onboarding
                steps: <strong>{"{{recipient_name}}"}</strong>, <strong>{"{{user_email}}"}</strong>,{" "}
                <strong>{"{{pc_username}}"}</strong>, <strong>{"{{temporary_password}}"}</strong>,{" "}
                <strong>{"{{one_time_password_link}}"}</strong>, <strong>{"{{company_name}}"}</strong>,{" "}
                <strong>{"{{psa_ticket_number}}"}</strong>. In HTML templates,{" "}
                <strong>{"{{one_time_password_link}}"}</strong> becomes a link with the text{" "}
                <strong>Click for Password</strong>. For Autotask, the ticket number is the display ID beginning with{" "}
                <strong>T</strong>, not the numeric internal ID.
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 2, fontSize: "0.8125rem" }}>
                The technician reviews and sends this from their own account.
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, fontFamily: "var(--theo-font-sans)" }}>
                Recipient
              </Typography>
              <RadioGroup
                value={localOptions.welcome_recipient ?? "new_user"}
                onChange={(e) => handleChange("welcome_recipient", e.target.value)}
                sx={{ mb: 2, "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.875rem" } }}
              >
                <FormControlLabel
                  value="new_user"
                  control={<Radio sx={{ "&.Mui-checked": { color: "#705D4A" } }} />}
                  label="New hire's personal / alternate email from the ticket (not the new M365 mailbox — they can't access it yet)"
                />
                <FormControlLabel
                  value="ticket_contact"
                  control={<Radio sx={{ "&.Mui-checked": { color: "#705D4A" } }} />}
                  label="Ticket contact / requester email from the ticket"
                />
              </RadioGroup>
              <TextField
                size="small"
                fullWidth
                label="CC recipients (optional)"
                placeholder="manager@example.com, hr@example.com"
                helperText={
                  <>
                    Comma-separated addresses. You may also use <strong>{"{{requester_email}}"}</strong> or{" "}
                    <strong>{"{{new_hire_personal_email}}"}</strong>.
                  </>
                }
                value={localOptions.welcome_cc_recipients ?? ""}
                onChange={(e) => handleChange("welcome_cc_recipients", e.target.value)}
                sx={{ ...textFieldSx, mb: 2 }}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                FormHelperTextProps={{ component: "div", style: { fontFamily: "var(--theo-font-sans)", fontSize: "0.75rem" } }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, fontFamily: "var(--theo-font-sans)" }}>
                Body format
              </Typography>
              <RadioGroup
                value={localOptions.welcome_body_format ?? "Text"}
                onChange={(e) => handleChange("welcome_body_format", e.target.value)}
                sx={{ mb: 2, "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.875rem" } }}
                row
              >
                <FormControlLabel value="Text" control={<Radio sx={{ "&.Mui-checked": { color: "#705D4A" } }} />} label="Plain text" />
                <FormControlLabel value="HTML" control={<Radio sx={{ "&.Mui-checked": { color: "#705D4A" } }} />} label="HTML" />
              </RadioGroup>
              <TextField
                size="small"
                fullWidth
                label="Subject template"
                value={localOptions.welcome_subject_template ?? ""}
                onChange={(e) => handleChange("welcome_subject_template", e.target.value)}
                sx={{ ...textFieldSx, mb: 2 }}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={12}
                maxRows={24}
                label="Body template"
                value={localOptions.welcome_body_template ?? ""}
                onChange={(e) => handleChange("welcome_body_template", e.target.value)}
                sx={textFieldSx}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mt: 2.5,
                  mb: 1,
                  color: "#555",
                  fontSize: "0.8125rem",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                Welcome letter PDF template
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 1.5, fontSize: "0.8125rem" }}>
                Optional skeleton PDF. Use placeholders such as <strong>{"{username}"}</strong>,{" "}
                <strong>{"{user_email}"}</strong>, <strong>{"{company_name}"}</strong> — or simply labeled lines with
                blank space after them (e.g. <strong>Email:</strong>, <strong>PC Username:</strong>,{" "}
                <strong>Password:</strong>, or a <strong>Hello</strong> greeting line); Theo writes the values into the
                empty space and leaves already-filled lines untouched. During onboarding Theo fills the letter with the
                new user&apos;s details and posts a download link in chat; the technician attaches the filled PDF to
                the welcome email before sending. Use <strong>{"{{one_time_password_link}}"}</strong> to render a
                clickable <strong>Click for Password</strong> link. Never include a raw password placeholder.
              </Typography>
              {localOptions.welcome_pdf_template_object ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PictureAsPdfIcon sx={{ fontSize: 20, color: "#B71C1C" }} />
                  <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "0.9375rem" }}>
                    {localOptions.welcome_pdf_template_filename || "welcome-letter-template.pdf"}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Remove PDF template"
                    onClick={() =>
                      setLocalOptions((prev) => ({
                        ...prev,
                        welcome_pdf_template_object: "",
                        welcome_pdf_template_filename: "",
                      }))
                    }
                    sx={{ color: "#666" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  component="label"
                  size="small"
                  disabled={uploadingPdfTemplate}
                  startIcon={uploadingPdfTemplate ? <CircularProgress size={16} /> : <UploadFileIcon />}
                  sx={{ fontFamily: "var(--theo-font-sans)", color: "#705D4A", textTransform: "none", border: "1px dashed #C9BEB2", px: 1.5 }}
                >
                  {uploadingPdfTemplate ? "Uploading…" : "Upload skeleton PDF"}
                  <input
                    hidden
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null;
                      e.target.value = "";
                      void handleWelcomePdfTemplateFile(file);
                    }}
                  />
                </Button>
              )}
              {pdfTemplateError && (
                <Typography variant="body2" sx={{ color: "#b3261e", mt: 0.75, fontSize: "0.8125rem" }}>
                  {pdfTemplateError}
                </Typography>
              )}
            </Box>
          )}

          {isItGluePassword && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#FAFAF9",
                border: "1px solid #F0EDE9",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: "#555",
                  fontSize: "0.8125rem",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                IT Glue password entry
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 2, fontSize: "0.8125rem" }}>
                Theo calls <strong>itglue_create_password</strong> after the user exists and the password is known.
                Use placeholders in templates: <strong>{"{{display_name}}"}</strong>,{" "}
                <strong>{"{{user_email}}"}</strong>, <strong>{"{{given_name}}"}</strong>,{" "}
                <strong>{"{{surname}}"}</strong>, <strong>{"{{upn}}"}</strong>. IT Glue{" "}
                <code>organization_id</code> must come from <strong>itglue_search_organizations</strong> — never
                Neo <code>customer_id</code>.
              </Typography>
              <TextField
                size="small"
                fullWidth
                label="Organization name hint (optional)"
                placeholder="e.g. Acme Corp — used to search IT Glue org"
                value={localOptions.itglue_organization_hint ?? ""}
                onChange={(e) => handleChange("itglue_organization_hint", e.target.value)}
                sx={{ ...textFieldSx, mb: 2 }}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
              <TextField
                size="small"
                fullWidth
                label="Password name template"
                value={localOptions.itglue_password_name_template ?? ""}
                onChange={(e) => handleChange("itglue_password_name_template", e.target.value)}
                sx={{ ...textFieldSx, mb: 2 }}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
              <TextField
                size="small"
                fullWidth
                label="Notes template (optional)"
                multiline
                minRows={2}
                value={localOptions.itglue_notes_template ?? ""}
                onChange={(e) => handleChange("itglue_notes_template", e.target.value)}
                sx={{ ...textFieldSx, mb: 2 }}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
              <TextField
                size="small"
                fullWidth
                label="Related URL (optional)"
                placeholder="e.g. https://office.com or tenant portal"
                value={localOptions.itglue_url ?? ""}
                onChange={(e) => handleChange("itglue_url", e.target.value)}
                sx={{ ...textFieldSx, mb: 2 }}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
              <TextField
                size="small"
                fullWidth
                label="Password folder name hint (optional)"
                placeholder="Resolve with itglue_search_password_folders"
                value={localOptions.itglue_password_folder_name ?? ""}
                onChange={(e) => handleChange("itglue_password_folder_name", e.target.value)}
                sx={{ ...textFieldSx, mb: 2 }}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
              <TextField
                size="small"
                fullWidth
                label="Password category name hint (optional)"
                placeholder="Resolve with itglue_search_password_categories"
                value={localOptions.itglue_password_category_name ?? ""}
                onChange={(e) => handleChange("itglue_password_category_name", e.target.value)}
                sx={{ ...textFieldSx, mb: 2 }}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                label="Extra instruction for Theo (optional)"
                value={localOptions.technician_instruction ?? ""}
                onChange={(e) => handleChange("technician_instruction", e.target.value)}
                sx={textFieldSx}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
            </Box>
          )}

          {isSetManager && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#FAFAF9",
                border: "1px solid #F0EDE9",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: "#555",
                  fontSize: "0.8125rem",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                Manager (Entra ID)
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 2, fontSize: "0.8125rem" }}>
                Theo uses <strong>m365_set_manager</strong> after the user exists. The new user&apos;s UPN comes from
                the create step; the manager is resolved with <strong>m365_search_users</strong> from the ticket or the
                hint below.
              </Typography>
              <TextField
                size="small"
                fullWidth
                label="Default manager hint (optional)"
                placeholder="e.g. manager@company.com or Jane Doe"
                value={localOptions.default_manager_hint ?? ""}
                onChange={(e) => handleChange("default_manager_hint", e.target.value)}
                sx={{ ...textFieldSx, mb: 2 }}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                label="Extra instruction for Theo (optional)"
                value={localOptions.technician_instruction ?? ""}
                onChange={(e) => handleChange("technician_instruction", e.target.value)}
                sx={textFieldSx}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
            </Box>
          )}

          {isAddToTeam && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#FAFAF9",
                border: "1px solid #F0EDE9",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: "#555",
                  fontSize: "0.8125rem",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                Microsoft Team
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 2, fontSize: "0.8125rem" }}>
                Theo uses <strong>m365_search_teams</strong> to resolve <code>team_id</code>, then{" "}
                <strong>m365_add_user_to_team</strong> with the new user&apos;s id from create_user. This is Teams
                membership, not Entra security groups.
              </Typography>
              <TextField
                size="small"
                fullWidth
                label="Default team name hint (optional)"
                placeholder="e.g. Engineering — used as m365_search_teams query prefix"
                value={localOptions.default_team_hint ?? ""}
                onChange={(e) => handleChange("default_team_hint", e.target.value)}
                sx={{ ...textFieldSx, mb: 2 }}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                label="Extra instruction for Theo (optional)"
                value={localOptions.technician_instruction ?? ""}
                onChange={(e) => handleChange("technician_instruction", e.target.value)}
                sx={textFieldSx}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
            </Box>
          )}

          {isEmailSignature && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#FAFAF9",
                border: "1px solid #F0EDE9",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: "#555",
                  fontSize: "0.8125rem",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                Outlook signature (Exchange Online)
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 2, fontSize: "0.8125rem" }}>
                Theo calls <strong>m365_set_email_signature</strong> for the new user&apos;s mailbox (UPN from the
                create-user step). Requires Exchange Online app authentication on the M365 integration. Merge
                placeholders from the ticket where helpful:{" "}
                <strong>{"{{display_name}}"}</strong>, <strong>{"{{user_email}}"}</strong>,{" "}
                <strong>{"{{job_title}}"}</strong>, <strong>{"{{company_name}}"}</strong>.
              </Typography>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={10}
                maxRows={28}
                label="Signature HTML template"
                placeholder="<p>...</p> (leave blank to derive from ticket / branding notes)"
                value={localOptions.signature_html_template ?? ""}
                onChange={(e) => handleChange("signature_html_template", e.target.value)}
                sx={{ ...textFieldSx, mb: 2 }}
                inputProps={{ style: { fontFamily: "monospace", fontSize: "0.8125rem" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontFamily: "var(--theo-font-sans)" }}>
                Auto-add signature
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  columnGap: 2,
                  rowGap: 1,
                  mb: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={localOptions.auto_add_to_new === "true"}
                      onChange={(e) => handleChange("auto_add_to_new", e.target.checked ? "true" : "false")}
                      sx={checkboxSx}
                    />
                  }
                  label="New messages"
                  sx={{
                    m: 0,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "var(--theo-font-sans)",
                      fontSize: "0.875rem",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={localOptions.auto_add_to_reply === "true"}
                      onChange={(e) => handleChange("auto_add_to_reply", e.target.checked ? "true" : "false")}
                      sx={checkboxSx}
                    />
                  }
                  label="Replies"
                  sx={{
                    m: 0,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "var(--theo-font-sans)",
                      fontSize: "0.875rem",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={localOptions.auto_add_to_forward === "true"}
                      onChange={(e) => handleChange("auto_add_to_forward", e.target.checked ? "true" : "false")}
                      sx={checkboxSx}
                    />
                  }
                  label="Forwards"
                  sx={{
                    m: 0,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "var(--theo-font-sans)",
                      fontSize: "0.875rem",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={localOptions.auto_add_on_mobile === "true"}
                      onChange={(e) => handleChange("auto_add_on_mobile", e.target.checked ? "true" : "false")}
                      sx={checkboxSx}
                    />
                  }
                  label="Mobile (where supported)"
                  sx={{
                    m: 0,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.875rem" },
                  }}
                />
              </Box>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                label="Extra instruction for Theo (optional)"
                value={localOptions.technician_instruction ?? ""}
                onChange={(e) => handleChange("technician_instruction", e.target.value)}
                sx={textFieldSx}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
            </Box>
          )}

          {!isSetPassword &&
            !isWelcomeEmail &&
            !isItGluePassword &&
            !isSetManager &&
            !isAddToTeam &&
            !isEmailSignature &&
            defaultFields.map((spec) => (
              <TextField
                key={spec.key}
                size="small"
                label={spec.label}
                placeholder={spec.placeholder}
                multiline={spec.multiline}
                minRows={spec.multiline ? spec.minRows ?? 4 : undefined}
                maxRows={spec.multiline ? 16 : undefined}
                value={localOptions[spec.key] ?? ""}
                onChange={(e) => handleChange(spec.key, e.target.value)}
                fullWidth
                sx={textFieldSx}
                inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              />
            ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1.5, borderTop: "1px solid #F0EDE9", bgcolor: "#FAFAF9" }}>
        <Button
          onClick={onClose}
          sx={{ fontFamily: "var(--theo-font-sans)", color: "#666", textTransform: "none", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}
        >
          Cancel
        </Button>
        <NeoButton variant="neo" onClick={handleSave}>
          Save
        </NeoButton>
      </DialogActions>
    </Dialog>
  );
};

export default StepConfigModal;
